// Pre-entries — frontend mirror of showman-expressjs-api/src/services/preEntries.js, which
// enforces the same rules on POST /addEntries.
//
// A pre-entry is paid now, with the animal's details added later (My Entry or check-in). Staff turn
// pre-entries on for an event (Setup → Product Controls → Pre-Entry Enabled, stored as
// event_settings.pre_entries_settings_enabled); that shows Pre-Entries in Event Settings → Entry
// Settings, where the host ticks Enable Pre-Entries (entry_settings.pre_entries.enabled) and sets
// each entry type (entry_settings.pre_entries.types, keyed by event_show_type_id) to:
//   off       as before (the default, and every type not listed)
//   optional  the entry form offers "Fill in now" (selected) or "Pre-entry"
//   only      every exhibitor entry is a pre-entry; an admin can still make a full entry
// Every show of an entry type follows it. With either switch off, every type is off.
// A pre-entry card (get_entry_group) carries pre_entry_key; every other card has none.

import {compareEntryTypes} from '@/utils/entryTypesTable'

// The class every pre-entry row has until it's finalized; it matches no class list (the server's
// PRE_ENTRY_CLASS_ID)
export const PRE_ENTRY_CLASS_ID = '913771b6-6d66-49f0-8514-da60e884b5af'

export const PRE_ENTRY_MODE_OPTIONS = [
  {label: 'Off', value: 'off', description: 'Exhibitors fill in the entry form when they enter.'},
  {label: 'Optional', value: 'optional', description: 'Exhibitors choose: fill in now, or pay now and add the details later.'},
  {label: 'Pre-Entry Only', value: 'only', description: 'Exhibitors pay now and add the details later. Admins can still fill in now.'}
]

// How many pre-entries one entry can add (the server's limit too)
export const MAX_PRE_ENTRIES = 50

export function preEntriesEnabled (event) {
  const value = event && event.event_settings && event.event_settings.pre_entries_settings_enabled
  return value === true || value === 'true'
}

// Whether an entry type can take pre-entries at all: not showmanship, which has no animal to add later
export function preEntryTypeOffered (entryType) {
  return !!entryType && !((entryType.label || '').toLowerCase().includes('showmanship'))
}

// The entry type's setting as stored, whether or not the event has pre-entries on
export function savedPreEntryMode (event, entryTypeId) {
  const types = (event && event.entry_settings && event.entry_settings.pre_entries && event.entry_settings.pre_entries.types) || {}
  const mode = types[entryTypeId]
  return PRE_ENTRY_MODE_OPTIONS.some(o => o.value === mode) ? mode : 'off'
}

// Whether the event takes pre-entries: staff turned them on and the host ticked Enable Pre-Entries
// (Event Settings → Entry Settings)
export function preEntriesOn (event) {
  return preEntriesEnabled(event) && !!preEntrySettings(event).enabled
}

// What an entry type takes in this event: off unless the event has pre-entries on
export function preEntryMode (event, entryType) {
  if (!preEntriesOn(event) || !preEntryTypeOffered(entryType)) return 'off'
  return savedPreEntryMode(event, entryType.event_show_type_id)
}

// Event Settings → Pre-Entries rows, in New Entry dropdown order: [{type, mode, offered}]
export function preEntryTypeRows (event, entryTypes) {
  return (entryTypes || [])
    .filter(t => t && t.event_show_type_id && !t.deleted_at)
    .slice()
    .sort(compareEntryTypes)
    .map(type => ({type, mode: savedPreEntryMode(event, type.event_show_type_id), offered: preEntryTypeOffered(type)}))
}

// What the entry form offers for an entry type of event:
//   offered   the form can make pre-entries
//   forced    it can only make pre-entries, unless isAdmin
export function preEntryChoice (entryType, {event, isAdmin = false} = {}) {
  const mode = preEntryMode(event, entryType)
  return {offered: mode !== 'off', forced: mode === 'only' && !isAdmin}
}

// The event's pre-entry settings beside the entry types (Event Settings → Pre-Entries)
function preEntrySettings (event) {
  return (event && event.entry_settings && event.entry_settings.pre_entries) || {}
}

// Today's date in the event's time zone, YYYY-MM-DD
function eventToday (event, now) {
  const timeZone = (event && event.event_settings && event.event_settings.time_zone) || 'America/Chicago'
  return new Intl.DateTimeFormat('en-CA', {timeZone, year: 'numeric', month: '2-digit', day: '2-digit'}).format(now)
}

// Whether an exhibitor can still finalize a pre-entry (admins always can). Mirrors the server's
// finalizeClosed. Finalizing is editing: open while online entries are, and after they close only
// if the event allows editing after close, or through the end of finalize_by when the host lets
// exhibitors finalize after close (finalize_after_close).
export function exhibitorCanFinalize (event, now = new Date()) {
  const settings = (event && event.entry_settings) || {}
  const pre = preEntrySettings(event)
  if (!entriesClosed(event, now)) return true
  if (pre.finalize_after_close && pre.finalize_by && eventToday(event, now) <= pre.finalize_by) return true
  return !!(settings.allow_edit_after_close || settings.allow_edit_after_close_strict)
}

// Whether the last day to submit online entries has passed, in the event's time zone
function entriesClosed (event, now) {
  const lastDay = event && event.entry_settings && event.entry_settings.last_day_of_online_entries
  return !!lastDay && eventToday(event, now) > lastDay
}

// Whether finalizing may move a pre-entry to another exhibitor on the account (admins always may).
// "Don't allow switching exhibitors" belongs to the after-close extension, so it applies only once
// entries have closed and only while finalize_after_close is on.
export function exhibitorSwitchAllowed (event, now = new Date()) {
  const pre = preEntrySettings(event)
  if (!pre.finalize_after_close || !pre.no_exhibitor_switch) return true
  return !entriesClosed(event, now)
}

// POST /finalizePreEntry's body from the entry form's entry (the card being finalized, with the
// details and class the form set): the same fields addEntries takes, plus the card's pre_entry_key
export function finalizePayload (entry) {
  return {
    pre_entry_key: entry.pre_entry_key,
    entry_details: entry.entry_details,
    class_id: entry.class_id,
    class_ids_by_show: entry.class_ids_by_show,
    division_by_show: entry.division_by_show,
    exhibitor_id: entry.exhibitor_id || (entry.exhibitor && entry.exhibitor.exhibitor_id) || null,
    animal_id: entry.animal ? entry.animal.animal_id : null
  }
}

export function isPreEntryCard (card) {
  return !!(card && card.pre_entry_key)
}

// "Pre-entry 1 of 3": a pre-entry card's place among its exhibitor's pre-entry cards, in list order
export function preEntryCardLabel (card, cards) {
  if (!isPreEntryCard(card)) return ''
  const exhibitorId = card.exhibitor_id
  const mine = (cards || []).filter(c => isPreEntryCard(c) && c.exhibitor_id === exhibitorId)
  const at = mine.findIndex(c => c.pre_entry_key === card.pre_entry_key)
  return mine.length > 1 && at > -1 ? `Pre-entry ${at + 1} of ${mine.length}` : 'Pre-entry'
}
